/**
 * Раскрывающийся блок: «главное видно, подробности на один тап глубже».
 *
 * Появился из простого наблюдения: карточка бюджета держала пятнадцать чисел,
 * и ответ на вопрос «успею ли» приходилось собирать из таблицы самому. Прятать
 * расчёт совсем нельзя — молчаливый вердикт вызывает недоверие, — поэтому он
 * не удаляется, а убирается под раскрытие.
 *
 * Почему не `<details>`: встроенный элемент не анимируется, по-разному
 * выглядит в браузерах и не даёт повесить свою стрелку без хаков с
 * `::marker`. Здесь та же семантика собрана руками — кнопка с
 * `aria-expanded` и связанная с ней область.
 */

import { ChevronDown } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { type ReactNode, useId, useState } from 'react';

import { cn } from '@/shared/lib/cn';

interface DisclosureProps {
  /**
   * Подпись кнопки: чем открывать, тем и называется. Может быть и целой
   * шапкой карточки — тогда карточка раскрывается нажатием на заголовок.
   */
  summary: ReactNode;
  children: ReactNode;
  /** Раскрыт при первом показе. По умолчанию закрыт — в этом весь смысл. */
  defaultOpen?: boolean;
  className?: string;
  /** Оформление кнопки — для шапки, которая выглядит не как подпись. */
  summaryClassName?: string;
}

export function Disclosure({
  summary,
  children,
  defaultOpen = false,
  className,
  summaryClassName,
}: DisclosureProps) {
  const [open, setOpen] = useState(defaultOpen);
  const contentId = useId();

  return (
    <div className={className}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={(event) => {
          // Блок часто стоит внутри карточки, которая сама открывается по
          // нажатию: без остановки всплытия раскрытие уводило бы с экрана.
          event.stopPropagation();
          setOpen((value) => !value);
        }}
        // Зона нажатия во всю ширину и 44 px в высоту: попадать в неё нужно
        // большим пальцем, а не прицеливаясь в стрелку.
        className={cn(
          'flex min-h-11 w-full items-center justify-between gap-2 rounded-md text-left',
          'text-[13px] text-muted transition-colors duration-150 active:text-content',
          summaryClassName,
        )}
      >
        {summary}
        <ChevronDown
          size={16}
          aria-hidden
          className={cn('shrink-0 transition-transform duration-200', open && 'rotate-180')}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={contentId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="pt-1 pb-0.5">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
